import { message } from '@firesoon/antd';
import { Copy, Information } from '@firesoon/icons-react';
import { Tooltip } from 'antd';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import React, { FC } from 'react';
import { TextProps } from './interface';
import Label from './label';
import './index.less';

/**
 * 提供一个富文本展示组件，支持多种样式和行为定制，例如删除、禁用、加粗、次级样式、链接、复制等功能。
 * 
 * @param color 文本颜色
 * @param del 是否显示删除样式
 * @param disabled 是否禁用状态
 * @param strong 是否加粗
 * @param negative 是否显示负面样式
 * @param secondary 是否显示次级样式
 * @param link 是否为链接，支持自定义属性
 * @param label 标签，支持字符串或标签组件
 * @param defaultText 默认展示文本
 * @param children 子元素
 * @param className 外层容器的类名
 * @param tooltips 文本的提示信息
 * @param copyable 是否可复制，支持配置复制的内容和回调
 * @param rest 其他属性
 * 
 * @returns 返回富文本组件
 */
const Text: FC<TextProps> = ({
  color,
  del = false,
  disabled = false,
  strong = false,
  negative = false,
  secondary = false,
  link,
  label,
  defaultText = '-',
  children,
  className,
  tooltips,
  copyable = false,
  maxWidth = 'auto',
  ...rest
}) => {
  /** 
   * 处理复制事件 
   * @param e 事件对象
   * 复制文本到剪贴板，并显示成功消息。
   */
  const handleCopy = (e) => {
    e.stopPropagation();
    if (typeof copyable === 'boolean') {
      copy(String(children));
    } else {
      const copyValue = copyable?.text || String(children);
      copy(copyValue);
      copyable?.onCopy?.(copyValue);
    }
    message.success(`复制成功`);
  };

  return (
    <div className={classnames('fs-text', className)} {...rest}>
      {(() => {
        // 根据传入的label渲染标签
        if (label) {
          if (typeof label === 'string') {
            return <Label>{label}</Label>;
          }
          return <Label {...label} />;
        }
      })()}

      {
        <span
          className={classnames('fs-text-content', {
            'fs-text-del': del,
            'fs-text-disabled': disabled,
            'fs-text-strong': strong,
            'fs-text-link': link,
            'fs-text-negative': negative,
            'fs-text-secondary': secondary,
            'fs-text-max-width': maxWidth,
          })}
          style={{ color, maxWidth: maxWidth }}
        >
          {(() => {
            // 处理链接逻辑，若为链接且未禁用，则添加额外样式和事件
            if (link && !disabled) {
              const { icon, tooltips, logValue = '文本点击', className, ...rest } = link || {};
              return (
                <>
                  <span {...rest} className={classnames(className, 'auto-track')} data-log-value={logValue}>
                    {children || defaultText}
                  </span>

                  <span className="fs-text-link-icon">
                    <Tooltip title={tooltips}>
                      {icon && React.cloneElement(icon as React.ReactElement)}
                    </Tooltip>
                  </span>
                </>
              );
            }
            return <span>{children || defaultText}</span>;
          })()}
        </span>
      }

      {(() => {
        // 根据tooltips渲染提示信息
        if (tooltips) {
          const _tooltips = typeof tooltips === 'string' ? { text: tooltips } : tooltips;
          return (
            <Tooltip title={_tooltips.text}>
              <span className="fs-text-tooltips-icon">{_tooltips?.icon || <Information />}</span>
            </Tooltip>
          );
        }

        // 根据copyable渲染复制图标和逻辑
        if (copyable) {
          const { logValue = '文本复制' } = typeof copyable === 'object' ? copyable : {};
          return (
            <span className="fs-text-copy-icon auto-track" onClick={handleCopy} data-log-value={logValue}>
              {typeof copyable !== 'boolean' && copyable?.icon ? copyable.icon : <Copy />}
            </span>
          );
        }
      })()}
    </div>
  );
};

export default Text;
